import { Drawer, Loading } from "@components";
import ComboFilters from "pages/Reports/ComboFilters";
import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import classNames from "classnames/bind";
import { getAllMonthsUntilNow } from "pages/Reports/Reports";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { ReportsAPI } from "providers/ReportsAPI";
import { useDrag } from "shared/hooks/useDrag";
const css = classNames.bind(styles);

const buildTable = (data) => {
  const statuses = {
    almoco: `ALMOÇO`,
    jantar: `JANTAR`,
    madrugada: `MADRUGADA`,
  };

  const rows = ["almoco", "jantar", "madrugada"];
  const columns = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];

  const table = rows.map((row) => {
    return {
      alias: row,
      status: statuses[row],
      values: columns.map((column) => {
        const rawValue = data?.[row]?.[column] ?? 0;

        return {
          label: column,
          tuple: `${row}__${column}`,
          value: rawValue,
        };
      }),
    };
  });

  return table;
};

export default function StageBehavior() {
  const getShade = (value, max) => {
    if (!value || +value === 0) {
      return {
        background: "#1e1e1f",
        color: "#808080",
      };
    }

    const intensity = Math.min(Math.max(value / max, 0), 1);
    const alpha = 0.6 + intensity * 1;

    return {
      background: `rgba(97, 97, 255, ${intensity})`,
      color: `rgba(255, 255, 255, ${alpha})`,
    };
  };

  const months = getAllMonthsUntilNow(false);

  const [hovered, setHovered] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("novos");
  const [loadingHistoric, setLoadingHistoric] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(months[0]);

  const filters = [
    { name: "Clientes novos", key: "novos" },
    { name: "Clientes em onboarding", key: "onboarding" },
    { name: "Clientes engajados", key: "engajados" },
  ];

  const provider = new ReportsAPI();

  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState(null);

  const fetchHistory = async ({ year, month }) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.lifeCycleHistory("behavior", year, month);

    const rows = Array(12).fill(0);

    results.forEach((result, index) => {
      const filterData = result[selectedFilter];

      const [refeicao, dia] = selectedData.alias.split("__");
      const value = filterData?.[refeicao]?.[dia] ?? 0;

      rows[index] = {
        value,
        growth: null,
      };
    });

    setLoadingHistoric(false);
    return rows;
  };

  const fetchData = async () => {
    setLoading(true);
    const response = await provider.lifeCycle(
      "behavior",
      selectedMonth.year,
      selectedMonth.month
    );
    setRawData(response);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const { scrollRef, onMouseDown } = useDragScroll();
  const scrollFiltersRef = useDrag(null);

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Comportamento dos estágios</p>
          <span
            className={styles.info}
            title="Momento da semana em que seus clientes de cada estágio pedem mais."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>

        <div className="d-flex align-center">
          <div className="d-flex align-center">
            <ComboFilters
              options={months}
              onSelect={(option) => setSelectedMonth(option)}
              selectedOption={selectedMonth}
            />
          </div>
        </div>
      </div>

      <div className="mb-15 mt-15">
        <div
          className={styles.boxFilters}
          style={{ paddingLeft: 0, paddingRight: 0 }}
          ref={scrollFiltersRef}
        >
          {filters.map((filter) => (
            <div
              key={filter.key}
              className={css({
                filter: true,
                selected: selectedFilter === filter.key,
              })}
              onClick={() => setSelectedFilter(filter.key)}
            >
              {filter.name}
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div
          className="d-flex align-center justify-center"
          style={{ height: 155 }}
        >
          <Loading />
        </div>
      ) : (
        <div
          className={styles.tableTransitions}
          onMouseDown={onMouseDown}
          ref={scrollRef}
        >
          <table>
            <thead>
              <tr>
                <th />
                <th>DOM</th>
                <th>SEG</th>
                <th>TER</th>
                <th>QUA</th>
                <th>QUI</th>
                <th>SEX</th>
                <th>SAB</th>
              </tr>
            </thead>
            <tbody>
              {buildTable(rawData?.[selectedFilter]).map((row) => (
                <tr key={row.status}>
                  <td className={styles.status} style={{ minWidth: 200 }}>
                    {row.status}
                  </td>
                  {row.values.map(({ tuple, label, value }, idx) => {
                    const allValues = buildTable(
                      rawData?.[selectedFilter]
                    ).flatMap((item) => item.values);

                    const maxItem = allValues.reduce((max, item) => {
                      return Number(item.value) > Number(max.value)
                        ? item
                        : max;
                    });

                    const style = getShade(value, Number(maxItem.value));

                    return (
                      <td
                        key={idx}
                        style={{
                          backgroundColor: style.background,
                          width: 230,
                          color: style.color,
                          ...(value && { cursor: "pointer" }),
                          ...(value &&
                            value === hovered?.alias && {
                              filter: "brightness(1.1)",
                            }),
                        }}
                        onMouseEnter={() =>
                          setHovered({
                            alias: `${tuple}`,
                            name: `${row.status}`,
                          })
                        }
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => {
                          if (value)
                            setSelectedData({
                              alias: `${tuple}`,
                              name: `${row.status} - ${label.toUpperCase()}`,
                            });
                        }}
                      >
                        {value || "---"}

                        <div
                          className={styles.chart}
                          style={{
                            ...(value &&
                              tuple === hovered?.alias && {
                                transform: "translateY(0)",
                                opacity: 1,
                              }),
                          }}
                        >
                          <i className="far fa-chart-waterfall" />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          onClose={() => setSelectedData(false)}
          loading={loadingHistoric}
          onFetchData={(...params) => fetchHistory(...params)}
          startOn2025
          skipCurrentMonth
        />
      </Drawer>
    </div>
  );
}
